import { useCallback, useContext, useRef } from 'react'
import SearchInput from '../../../components/SearchInput'
import { useI18n } from '../../../provider/i18n'
import { useAtomValue } from 'jotai'
import { profilesSubscriptionAtom } from '../../../store/subscription.atoms'
import { BasketContext, isConsultationBasketItem } from '../../../provider/basket'
import { AuthContext } from '../../../provider/auth'
import { Link, useSearchParams } from 'react-router-dom'
import ProfileSelectorModal, { ProfileSelectorModalType } from '../../../components/ProfileSelectorModal'
import { ConsultationFragment, useGetConsultationsByQueryQuery } from '../../../generated/urql.anonymous'
import { TailSpin } from 'react-loader-spinner'
import { Blue300 } from '../../../lib/colors'
import { ConsultationItem } from '../../../components/ConsultationItem'

const SearchConsultations = () => {
  const [params] = useSearchParams()
  const basket = useContext(BasketContext)
  const auth = useContext(AuthContext)
  const i18n = useI18n()
  const [{ data: profilesData }] = useAtomValue(profilesSubscriptionAtom)
  const profileSelectorModalRef = useRef<ProfileSelectorModalType>(null)
  const query = params.get('q')
  const [{ data: consultationsData, fetching: isFetching }] =
    useGetConsultationsByQueryQuery({
      pause: !query,
      variables: {
        limit: 1000,
        query: query || '',
      },
    })

  const onConsultationAddClick = useCallback(
    (consultation: ConsultationFragment) => {
      const profilesCount = profilesData?.client_profile.length || 0

      if (!auth.isLogged || profilesCount === 0) {
        basket.addItem({ consultation })
      } else if (profilesCount === 1) {
        basket.addItem({
          consultation,
          profile: profilesData?.client_profile[0],
        })
      } else {
        profileSelectorModalRef.current?.show({
          onChange: (profile) => {
            basket.addItem({ consultation, profile })
          },
          filter: (profile) =>
            !basket.items.find(
              (item) =>
                isConsultationBasketItem(item) &&
                item.consultation.id === consultation.id &&
                item.profile?.id === profile.id
            ),
        })
      }
    },
    [auth.isLogged, basket, profilesData?.client_profile]
  )

  const consultations = consultationsData?.search_consultation || []

  return (
    <div>
      <SearchInput />
      <div className="text-blue-700 my-5 flex gap-1 items-center">
        <span>
          <Link to="/consultation">
            {i18n.t('screens.catalog.index.consultations.header')}
          </Link>
          {' > '}
          {i18n.t('screens.catalog.search.breadcrumbs.searchResults', {
            query: query || '',
          })}
        </span>
        {isFetching && <TailSpin color={Blue300} height={16} width={16} />}
      </div>
      <ul className="md:mx-5 flex flex-col gap-5 mb-10">
        {consultations.map((consultation) => (
          <li key={consultation.id}>
            <ConsultationItem
              consultation={consultation}
              onConsultationAddClick={() =>
                onConsultationAddClick(consultation)
              }
              showAddButton={
                auth.isLogged
                  ? basket.items
                      .filter(isConsultationBasketItem)
                      .filter(
                        ({ consultation: { id } }) => consultation.id === id
                      ).length < (profilesData?.client_profile.length || 0)
                  : !basket.items.find(
                      (item) =>
                        isConsultationBasketItem(item) &&
                        consultation.id === item.consultation.id
                    )
              }
            />
          </li>
        ))}
      </ul>
      <ProfileSelectorModal ref={profileSelectorModalRef} />
    </div>
  )
}

export default SearchConsultations
